'use client';

import { c as _c } from "react/compiler-runtime";
import { CaretDown, type IconProps } from '@phosphor-icons/react';
import type { Transition, Variants } from 'motion/react';
import * as m from 'motion/react-m';
import type { ComponentType, ElementType } from 'react';
import { type PropsWithChildren, type ReactNode, useState } from 'react';
import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';
import { cx } from '../../cx';
import { IconButton } from '../button/icon-button';
const variants = tv({
  slots: {
    base: 'rounded-xl p-4 sm:px-6',
    summary: 'flex cursor-pointer flex-col group',
    header: 'flex w-full items-center justify-between',
    title: 'flex items-center gap-2',
    content: 'font-body-small overflow-hidden'
  },
  variants: {
    color: {
      lightblue: {
        base: 'bg-lightblue'
      },
      ghost: {
        base: 'px-0 sm:px-0'
      }
    },
    titleVariant: {
      bold: {
        title: 'font-heading5 uppercase'
      },
      normal: {
        title: 'font-body-copy'
      },
      heading: {
        title: 'font-heading3'
      }
    }
  },
  defaultVariants: {
    color: 'lightblue',
    titleVariant: 'bold'
  }
});
const detailsAnimation = {
  variants: {
    expanded: {
      height: 'auto',
      opacity: 1
    },
    collapsed: {
      height: '0',
      opacity: 0
    }
  } as Variants,
  transition: {
    ease: 'circOut',
    duration: 0.4,
    opacity: {
      duration: 0.5
    }
  } as Transition
};
export type AccordionProps = VariantProps<typeof variants> & PropsWithChildren<{
  title: ReactNode;
  titleAs?: ElementType;
  subTitle?: ReactNode;
  Icon?: ComponentType<IconProps>;
  expanded?: boolean;
  footer?: ReactNode;
}>;
export function Accordion(t0) {
  const $ = _c(29);
  let Icon;
  let children;
  let footer;
  let props;
  let subTitle;
  let t1;
  let t2;
  let title;
  if ($[0] !== t0) {
    ({
      title,
      titleAs: t1,
      subTitle,
      Icon,
      expanded: t2,
      children,
      footer,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = children;
    $[3] = footer;
    $[4] = props;
    $[5] = subTitle;
    $[6] = t1;
    $[7] = t2;
    $[8] = title;
  } else {
    Icon = $[1];
    children = $[2];
    footer = $[3];
    props = $[4];
    subTitle = $[5];
    t1 = $[6];
    t2 = $[7];
    title = $[8];
  }
  const Title = t1 === undefined ? "h5" : t1;
  const expanded = t2 === undefined ? false : t2;
  const classes = variants({
    ...props
  });
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [lastExpandedProp, setLastExpandedProp] = useState(expanded);
  if (lastExpandedProp !== expanded) {
    setLastExpandedProp(expanded);
    setIsExpanded(expanded);
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = event => setIsExpanded(event.currentTarget.open);
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleToggle = t3;
  const t4 = classes.base();
  const t5 = classes.summary();
  const t6 = classes.header();
  let t7;
  if ($[10] !== Icon) {
    t7 = Icon && <Icon size={24} weight="bold" />;
    $[10] = Icon;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== isExpanded) {
    t8 = cx("transform transition-transform", {
      "rotate-180": isExpanded
    });
    $[12] = isExpanded;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = isExpanded ? "Einklappen" : "Ausklappen";
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <IconButton as="span" iconClassName={t8} Icon={CaretDown} aria-label={t9} iconOnly={true} size="small" className="group-hover:layer-bg-hover-light" />;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== subTitle) {
    t11 = subTitle && <div className="mt-1 font-body-small">{subTitle}</div>;
    $[17] = subTitle;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = m;
  const t13 = isExpanded ? "expanded" : "collapsed";
  const t14 = detailsAnimation;
  const t15 = classes.content();
  let t16;
  if ($[19] !== children) {
    t16 = <div className="mt-6">{children}</div>;
    $[19] = children;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== t12.div || $[22] !== t13 || $[23] !== t14 || $[24] !== t15 || $[25] !== t16) {
    t17 = <t12.div animate={t13} {...t14} className={t15}>{t16}</t12.div>;
    $[21] = t12.div;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== footer) {
    t18 = footer && <div className="">{footer}</div>;
    $[27] = footer;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  return <div className={t4}><details open={isExpanded} onToggle={handleToggle}><summary className={t5}><div className={t6}><Title className={classes.title()}>{t7}{title}</Title>{t10}</div>{t11}</summary>{t17}</details>{t18}</div>;
}